<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import { getLocaleMessages } from "@/locales/index"
import router from "@/plugins/router"
import qualityService from "@/services/quality"
import type { Option } from "@/types/common.type"
import type { QualityDetail } from "@/types/quality.type"
import { reportSentry } from "@/utils/sentry"

const route = useRoute()
const { t } = useI18n()

const item = ref<QualityDetail | null>(null)

const id = computed(() => route.params.id)

const isSubmitting = ref(false)
const errorMessage = ref("")

const payload = ref({
  isSwatchOffered: null as boolean | null,
  reason: "",
})

const isSwatchOfferedOptions = computed(() => {
  const options = [] as Option[]
  const isSwatchOfferedList = getLocaleMessages().item.isSwatchOfferedList
  for (const key in isSwatchOfferedList)
    options.push({ value: key, label: isSwatchOfferedList[key as keyof typeof isSwatchOfferedList] })
  return options
})

async function loadItem() {
  item.value = await qualityService.get(Number(id.value))
  payload.value.isSwatchOffered = item.value.isSwatchOffered
}

async function submitForm() {
  isSubmitting.value = true

  try {
    await qualityService.updateSwatchOffered(Number(id.value), {
      isSwatchOffered: payload.value.isSwatchOffered!,
      reason: payload.value.reason,
    })

    router.push(`/items/${id.value}`)
  } catch (err) {
    console.log(err)
    reportSentry(err)
    errorMessage.value = t("shared.errors.unknown")
    isSubmitting.value = false
  }

  router.push(`/swatch_stocks`)
}

// initial load
onMounted(async () => {
  try {
    await loadItem()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})
</script>

<template>
  <LayoutAuthed :title="$t('nav.swatchStocks')">
    <form @submit.prevent="submitForm()">
      <div class="row">
        <div class="col-12 col-lg-6 col-xxl-4 mb-4">
          <Card>
            <CardTitle>{{ $t("item.isSwatchOffered") }}</CardTitle>
            <CardContent>
              <InputSelect
                v-model="payload.isSwatchOffered"
                type="text"
                field="isSwatchOffered"
                :label="$t('item.isSwatchOffered')"
                :options="isSwatchOfferedOptions"
                required
              />
              <InputBox
                v-if="String(payload.isSwatchOffered) === 'false'"
                v-model="payload.reason"
                type="text"
                field="reason"
                :label="$t('item.swatchNotOfferReason')"
              />
            </CardContent>
            <CardActions displayBlock>
              <Button type="submit" color="dark" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
              <span class="text-center text-danger error-message">{{ errorMessage }}</span>
            </CardActions>
          </Card>
        </div>
      </div>
    </form>
  </LayoutAuthed>
</template>

<style scoped></style>
